import { useEffect, useRef, useState } from "react"
import {
  deleteStaffTemplateCategory,
  listStaffTemplateCategories,
  upsertStaffTemplateCategory,
} from "~/api/staff"
import {
  getNextCategoryRank,
  loadTemplateCategoryRecords,
} from "~/common/videoTemplate/categories"
import { saveCategoryOrder, type CategoryOrder } from "./categorySorting"

export const useStaffTemplateCategories = () => {
  const [items, setItems] = useState<API.VideoTemplate.Category[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState("")
  const [updating, setUpdating] = useState("")
  const [order, setOrder] = useState<CategoryOrder | null>(null)
  const [savingOrder, setSavingOrder] = useState(false)
  const [orderError, setOrderError] = useState("")
  const [error, setError] = useState("")
  const [revision, setRevision] = useState(0)
  const mutation = useRef<AbortController | null>(null)
  const categories = order?.categories ?? items.map((item) => item.category)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError("")
    void loadTemplateCategoryRecords(
      { signal: controller.signal },
      (params, options) => listStaffTemplateCategories(params, options?.signal),
    )
      .then((records) => {
        if (!controller.signal.aborted) {
          setItems(records)
          setError("")
        }
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : "分类加载失败")
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [revision])

  useEffect(() => () => mutation.current?.abort(), [])

  const create = async (value: string): Promise<string> => {
    const category = value.trim()
    if (!category) throw new Error("请输入分类名称")
    if (mutation.current || loading) throw new Error("分类正在处理，请稍候")
    if (order) throw new Error("请先保存或取消分类排序")
    if (error) throw new Error("请先重新加载分类")
    if (categories.includes(category)) return category
    const rank = getNextCategoryRank(items)
    const controller = new AbortController()
    mutation.current = controller
    setCreating(true)
    try {
      const { data } = await upsertStaffTemplateCategory(
        { category, rank },
        controller.signal,
      )
      if (data?.category !== category)
        throw new Error("分类创建返回数据无效，请重试")
      if (!controller.signal.aborted)
        setItems((current) =>
          current.some((item) => item.category === category)
            ? current
            : [...current, { ...data, rank }],
        )
      return category
    } finally {
      mutation.current = null
      if (!controller.signal.aborted) setCreating(false)
    }
  }

  const remove = async (category: string): Promise<void> => {
    if (mutation.current || loading) throw new Error("分类正在处理，请稍候")
    if (order) throw new Error("请先保存或取消分类排序")
    if (!category.trim() || !categories.includes(category))
      throw new Error("该分类已不存在，请重新加载列表")
    const controller = new AbortController()
    mutation.current = controller
    setDeleting(category)
    try {
      await deleteStaffTemplateCategory({ category }, controller.signal)
      if (!controller.signal.aborted)
        setItems((current) =>
          current.filter((item) => item.category !== category),
        )
    } finally {
      mutation.current = null
      if (!controller.signal.aborted) setDeleting("")
    }
  }

  const updateRank = async (category: string, rank: number): Promise<void> => {
    if (mutation.current || loading) throw new Error("分类正在处理，请稍候")
    if (order) throw new Error("请先保存或取消分类排序")
    if (error) throw new Error("请先重新加载分类")
    if (!categories.includes(category))
      throw new Error("该分类已不存在，请重新加载列表")
    if (!Number.isFinite(rank)) throw new Error("请输入有效的排序值")
    const controller = new AbortController()
    mutation.current = controller
    setUpdating(category)
    try {
      const { data } = await upsertStaffTemplateCategory(
        { category, rank },
        controller.signal,
      )
      if (
        data?.category !== category ||
        (data.rank !== undefined && data.rank !== rank)
      )
        throw new Error("分类排序返回数据无效，请重试")
      if (!controller.signal.aborted) {
        setItems((current) =>
          current.map((item) =>
            item.category === category ? { ...item, rank } : item,
          ),
        )
        setLoading(true)
        setRevision((value) => value + 1)
      }
    } finally {
      mutation.current = null
      if (!controller.signal.aborted) setUpdating("")
    }
  }

  const reorder = (from: string, to: string): void => {
    if (mutation.current || loading || error || order?.retry || from === to)
      return
    const start = categories.indexOf(from)
    const end = categories.indexOf(to)
    if (start < 0 || end < 0) return
    const next = [...categories]
    next.splice(end, 0, ...next.splice(start, 1))
    const baseline = order?.baseline ?? items
    setOrder(
      next.every((category, index) => category === baseline[index]?.category)
        ? null
        : { categories: next, baseline },
    )
    setOrderError("")
  }

  const saveOrder = async (): Promise<void> => {
    if (mutation.current || loading) throw new Error("分类正在处理，请稍候")
    if (!order) return
    const controller = new AbortController()
    mutation.current = controller
    setSavingOrder(true)
    setOrderError("")
    try {
      await saveCategoryOrder(order, controller.signal)
      if (!controller.signal.aborted) {
        setOrder(null)
        setLoading(true)
        setRevision((value) => value + 1)
      }
    } catch (cause) {
      if (!controller.signal.aborted) {
        setOrder({ ...order, retry: true })
        setOrderError(
          cause instanceof Error ? cause.message : "分类顺序保存失败",
        )
      }
      throw cause
    } finally {
      mutation.current = null
      if (!controller.signal.aborted) setSavingOrder(false)
    }
  }

  const cancelOrder = (): void => {
    if (mutation.current) return
    setOrder(null)
    setOrderError("")
    setLoading(true)
    setRevision((value) => value + 1)
  }

  return {
    items,
    categories,
    loading,
    creating,
    deleting,
    updating,
    dirty: Boolean(order),
    orderFailed: Boolean(order?.retry),
    savingOrder,
    orderError,
    error,
    create,
    remove,
    updateRank,
    reorder,
    saveOrder,
    cancelOrder,
    reload: () => {
      if (!mutation.current && !order) setRevision((value) => value + 1)
    },
  }
}
